import { flatten, renderTemplate } from "@wapadesk/engine";
export const DEFAULT_TEMPLATE = {
    greeting: "Hello, I'm contacting you from your website.",
    closing: "",
    order: [],
    labels: {},
    includeRecordLink: false,
    includePage: true,
};
export const TARGET_LENGTH = 1500;
const ELLIPSIS = "…";
const isEmpty = (v) => v === undefined || v === null || v === "" || (Array.isArray(v) && v.length === 0);
const ZERO_WIDTH_SPACE = String.fromCharCode(0x200b);
/** WhatsApp treats * and _ as formatting; keep patient text literal. */
function literal(s) {
    return s.replace(/[*_~`]/g, (c) => ZERO_WIDTH_SPACE + c).replace(/\r?\n/g, "\n");
}
function optionLabel(node, id, extra) {
    if (id.startsWith("other:"))
        return id.slice(6);
    if ((node.type === "single_choice" || node.type === "multi_choice") && node.options) {
        const o = node.options.find((x) => x.id === id);
        if (o)
            return o.label;
    }
    return extra?.[id] ?? id;
}
function formatValue(node, v, extra, fileCount = 0) {
    switch (node.type) {
        case "consent":
        case "message":
            return undefined;
        case "file_upload": {
            const n = Array.isArray(v) ? v.length : fileCount;
            if (n === 0)
                return undefined;
            return n === 1 ? "1 file" : `${n} files`;
        }
        case "single_choice":
            return typeof v === "string" ? optionLabel(node, v, extra) : undefined;
        case "multi_choice":
        case "procedure_picker":
            return Array.isArray(v)
                ? v.map((x) => optionLabel(node, x, extra)).join(", ")
                : typeof v === "string"
                    ? optionLabel(node, v, extra)
                    : undefined;
        case "number":
            return String(v);
        case "date":
            return typeof v === "string" ? v.split("-").reverse().join("/") : String(v);
        default:
            return typeof v === "boolean"
                ? v
                    ? "Yes"
                    : "No"
                : Array.isArray(v)
                    ? v.join(", ")
                    : typeof v === "object"
                        ? undefined
                        : String(v);
    }
}
function defaultLabel(node) {
    if (node.message?.label)
        return node.message.label;
    const p = (node.prompt ?? node.id).replace(/\{\{[^}]*\}\}/g, "").trim();
    return p.replace(/[?:.]+$/, "").slice(0, 60) || node.id;
}
export function collectFields(input) {
    const fields = [];
    for (const f of flatten(input.form)) {
        const node = f.node;
        if (node.message?.include === false)
            continue;
        const raw = input.answers[node.id];
        if (isEmpty(raw) && node.type !== "file_upload")
            continue;
        const value = formatValue(node, raw ?? [], input.optionLabels, input.lead.fileCount);
        if (value === undefined || value === "")
            continue;
        fields.push({
            stepId: f.stepId,
            nodeId: node.id,
            label: input.template.labels[node.id] ?? defaultLabel(node),
            value,
            freeText: node.type === "text" || node.type === "long_text",
        });
    }
    if (input.template.order.length) {
        const rank = new Map(input.template.order.map((id, i) => [id, i]));
        fields.sort((a, b) => (rank.get(a.nodeId) ?? Number.MAX_SAFE_INTEGER) -
            (rank.get(b.nodeId) ?? Number.MAX_SAFE_INTEGER));
    }
    return fields;
}
/** "*Appointment:* Free 15-minute online consultation with Dr Smith\n*When:* Tue 20 Oct, 10:00 (Europe/London)". */
export function appointmentLines(a) {
    const d = a.startAt instanceof Date ? a.startAt : new Date(a.startAt);
    let when;
    try {
        when = d.toLocaleString("en-GB", {
            weekday: "short",
            day: "numeric",
            month: "short",
            hour: "2-digit",
            minute: "2-digit",
            timeZone: a.timezone,
        });
    }
    catch {
        when = d.toISOString();
    }
    return [
        `*Appointment:* ${literal(a.typeName)}${a.doctorName ? ` with ${literal(a.doctorName)}` : ""} (${a.mode === "online" ? "online" : "in person"})`,
        `*When:* ${when} (${a.timezone})`,
        ...(a.mode === "in_person" && a.location ? [`*Where:* ${literal(a.location)}`] : []),
        ...(a.procedures?.length ? [`*Services:* ${a.procedures.map(literal).join(", ")}`] : []),
    ].join("\n");
}
/** Name, phone and email from the lead record that no answered question already prints. */
function contactLines(input) {
    const c = input.contact;
    if (!c)
        return [];
    const answered = new Set();
    for (const f of flatten(input.form))
        if (f.node.maps && !isEmpty(input.answers[f.node.id]))
            answered.add(f.node.maps);
    const hasName = answered.has("contact.fullName") ||
        answered.has("contact.firstName") ||
        answered.has("contact.lastName");
    const out = [];
    if (c.name?.trim() && !hasName)
        out.push(`*Name:* ${literal(c.name.trim())}`);
    if (c.phone?.trim() && !answered.has("contact.phone"))
        out.push(`*Phone:* ${literal(c.phone.trim())}`);
    if (c.email?.trim() && !answered.has("contact.email"))
        out.push(`*Email:* ${literal(c.email.trim())}`);
    return out;
}
function assemble(input, fields) {
    const ctx = { clinic: input.clinic, answers: input.answers };
    const parts = [];
    const greeting = renderTemplate(input.template.greeting, ctx).trim();
    if (greeting)
        parts.push(greeting);
    const contact = contactLines(input);
    if (contact.length)
        parts.push(contact.join("\n"));
    let group = [];
    let currentStep;
    const ordered = input.template.order.length > 0;
    for (const f of fields) {
        if (!ordered && currentStep !== undefined && f.stepId !== currentStep && group.length) {
            parts.push(group.join("\n"));
            group = [];
        }
        currentStep = f.stepId;
        group.push(`*${literal(f.label)}:* ${literal(f.value)}`);
    }
    if (group.length)
        parts.push(group.join("\n"));
    if (input.appointment)
        parts.push(appointmentLines(input.appointment));
    if (input.note?.trim())
        parts.push(`*My note:* ${literal(input.note.trim())}`);
    const tail = [];
    // Which page they wrote from (owner request, 14 October 2026): the team sees the context at once.
    // Only the path, never the full address: WhatsApp would turn a URL into a preview card of the
    // clinic's own page under the patient's message (owner request, 14 October 2026).
    const page = input.template.includePage !== false && input.pageUrl ? pageLabel(input.pageUrl) : undefined;
    if (page)
        tail.push(`From page: ${page}`);
    if (input.template.includeRecordLink && input.recordUrl)
        tail.push(`View record: ${input.recordUrl}`);
    const closing = renderTemplate(input.template.closing, ctx).trim();
    if (closing)
        tail.push(closing);
    tail.push(`Ref: ${input.lead.ref}`);
    parts.push(tail.join("\n"));
    return parts.join("\n\n");
}
/** Renders the pre-filled WhatsApp message. */
export function renderWhatsAppMessage(input) {
    const max = input.maxLength ?? TARGET_LENGTH;
    const fields = collectFields(input);
    let text = assemble(input, fields);
    if (text.length <= max)
        return text;
    // Truncate free-text answers, longest first, until it fits. Labelled fields are never dropped.
    const free = fields.filter((f) => f.freeText).sort((a, b) => b.value.length - a.value.length);
    for (const f of free) {
        const over = text.length - max;
        if (over <= 0)
            break;
        const keep = Math.max(20, f.value.length - over - ELLIPSIS.length);
        if (keep >= f.value.length)
            continue;
        f.value = f.value.slice(0, keep).trimEnd() + ELLIPSIS;
        text = assemble(input, fields);
    }
    return text;
}
/** Sample data for the dashboard preview (7.2). */
const SAMPLE_TEXT = {
    "contact.fullName": "Ayse Yilmaz",
    "contact.firstName": "Ayse",
    "contact.lastName": "Yilmaz",
    "contact.location": "London, UK",
    "contact.country": "United Kingdom",
    "contact.city": "London",
};
export function sampleAnswersFor(form) {
    const out = {};
    for (const f of flatten(form)) {
        const n = f.node;
        switch (n.type) {
            case "consent":
                out[n.id] = Object.fromEntries(n.items.map((i) => [i.key, true]));
                break;
            case "text":
                out[n.id] = SAMPLE_TEXT[n.maps ?? ""] ?? "Sample answer";
                break;
            case "long_text":
                out[n.id] = "Could you tell me more about prices and your next available times?";
                break;
            case "email":
                out[n.id] = "ayse@example.com";
                break;
            case "phone":
                out[n.id] = "+447700900123";
                break;
            case "number":
                out[n.id] = 34;
                break;
            case "date":
                out[n.id] = "1992-03-12";
                break;
            case "single_choice":
                out[n.id] = n.options[0]?.id ?? "";
                break;
            case "multi_choice":
                out[n.id] = n.options.slice(0, 2).map((o) => o.id);
                break;
            case "procedure_picker":
                out[n.id] = ["General enquiry", "Prices"];
                break;
            case "file_upload":
                out[n.id] = ["f1", "f2", "f3"];
                break;
            default:
                break;
        }
    }
    return out;
}
/**
 * "/mole-removal" for a page on the clinic's site, "home page" for the root, "share link" for the
 * Wapadesk share link. The query string and fragment are dropped (they can hold personal data).
 */
export function pageLabel(url) {
    // No URL class here: this package runs in the widget, the dashboard and functions alike.
    const m = /^https?:\/\/([^/?#\s]+)([^?#\s]*)/i.exec(url.trim());
    if (!m)
        return undefined;
    const host = m[1].toLowerCase().replace(/:\d+$/, "");
    if (/(^|\.)widget(-staging)?\.wapadesk\.com$/.test(host))
        return "share link";
    let path = (m[2] ?? "").replace(/\/+$/, "");
    try {
        path = decodeURIComponent(path);
    }
    catch {
        /* keep it encoded */
    }
    return path ? path.slice(0, 120) : "home page";
}
//# sourceMappingURL=render.js.map